import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AdminDodDefinitionService } from "../../../../api/services/admin-dod-definition.service";
import { AdminDodTestService } from "../../../../api/services/admin-dod-test.service";
import { DodDefinitionResponse } from "../../../../api/models/dod-definition-response";
import { DodDefinitionIssue } from "../../../../api/models/dod-definition-issue";
import { DodEndPoint } from "../../../../api/models/dod-end-point";
import { DodEndPointSubData } from "../../../../api/models/dod-end-point-sub-data";
import { DodEndPointColumn } from "../../../../api/models/dod-end-point-column";
import { DodEndPointDimension } from "../../../../api/models/dod-end-point-dimension";
import { DodEndPointMeasure } from "../../../../api/models/dod-end-point-measure";
import { DodEndPointAggregateType } from "../../../../api/models/dod-end-point-aggregate-type";
import { DodSaveResult } from "../../../../api/models/dod-save-result";
import { DialogResultType } from "../../../../dialogs/DialogResult";
import { DodEditDialogComponent, DodField } from "../../../../dialogs/dod-edit-dialog/dod-edit-dialog.component";
import { DodRenameDialogComponent } from "../../../../dialogs/dod-rename-dialog/dod-rename-dialog.component";

// The numeric unions mirror the C# enum member order (DodRowKind / DodRenameKind in PublicInsight.BackgroundTasks/Backend/Dod,
// EndPointType in PublicInsight.Db/Models/DodEndPoint.cs).
export const ROW_KIND = { endPoint: 0, subData: 1, column: 2, dimension: 3, measure: 4, aggregateType: 5 } as const;
export const RENAME_KIND = { columnSelector: 0, measureKey: 1, dimensionKey: 2, subDataKey: 3, columnName: 4 } as const;
export const ENDPOINT_TYPES = [{ value: 0, label: "Standard" }, { value: 1, label: "Analytical" }];

export interface ColumnGroup { name: string; columns: DodEndPointColumn[]; }
export interface MeasureRow { measure: DodEndPointMeasure; aggregates: string; }

// Admin authoring of the Data on Demand definition. Modeled on the ADM Tables Definition editor: every save returns the
// current validation issues, nothing is blocked by an incomplete definition, and a selector/key that saved layouts use
// can only be changed through Rename, which rewrites those layouts. Derived lists are built once per load / selection
// change (never in getters or template calls) so *ngFor keeps stable DOM identity.
@Component({
  selector: "admin-dod-definition",
  templateUrl: "./admin-dod-definition.component.html",
})
export class AdminDodDefinitionComponent implements OnInit, OnDestroy {
  public loading = true;
  public definition: DodDefinitionResponse = { endPoints: [], subData: [], columns: [], dimensions: [], measures: [], aggregateTypes: [], measureAggregates: [], endPointFilters: [], filterTypes: [], planTypeLinks: [], issues: [] };
  public issues: DodDefinitionIssue[] = [];
  public readonly kinds = ROW_KIND;

  public selectedEndPointId: number | null = null;
  public showRetired = false;
  // 0 Columns, 1 Measures, 2 Dimensions, 3 Sub-data, 4 Filters, 5 Aggregates, 6 Import / Export, 7 Validation
  public selectedTab = 0;
  public readonly validationTabIndex = 7;

  // ---- derived (rebuilt by refresh()) ----
  public endPointOptions: { id: number; label: string }[] = [];
  public selectedEndPoint: DodEndPoint | null = null;
  public scopeOptions: { value: string; label: string }[] = [];
  // "e" = the endpoint's own columns, "s:<id>" = a sub-data's columns
  public columnScope = "e";
  public columnGroups: ColumnGroup[] = [];
  public measureRows: MeasureRow[] = [];
  public dimensionRows: DodEndPointDimension[] = [];
  public subDataRows: DodEndPointSubData[] = [];
  public aggregateRows: DodEndPointAggregateType[] = [];
  public visibleIssues: DodDefinitionIssue[] = [];
  public issueFilter: "all" | "errors" | "warnings" = "all";
  public endPointIssueCount = 0;

  // ---- column selection / bulk ----
  public selectedColumnIds = new Set<number>();
  public moveTargetValue = "";
  public moveTargets: { value: string; label: string }[] = [];
  public bulkGroup = "";
  public working = false;

  // ---- filters tab ----
  public filterSelection = new Set<number>();

  // ---- test export ----
  public testStarting = false;
  // Whether the selected endpoint, or any of its sub-data, has a test running (the poll keeps going while anything is).
  public testRunning = false;
  private testPoll: any = null;

  constructor(private service: AdminDodDefinitionService, private testService: AdminDodTestService, private snackBar: MatSnackBar, private dialog: MatDialog) {}

  ngOnInit() {
    this.load();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  // `silent` is the background poll while a test runs: no "Loading..." flicker.
  load(silent = false) {
    if (!silent) this.loading = true;
    this.service.adminDodDefinitionGetDefinition().subscribe({
      next: (definition) => {
        this.definition = definition;
        this.issues = definition.issues || [];
        const endPoints = definition.endPoints || [];
        if (this.selectedEndPointId === null || !endPoints.some((e) => e.dodEndPointId === this.selectedEndPointId)) {
          const first = endPoints.find((e) => !e.retired) || endPoints[0];
          this.selectedEndPointId = first ? first.dodEndPointId : null;
          this.columnScope = "e";
        }
        this.refresh();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open("Could not load the DoD definition", null, { duration: 5000 });
      },
    });
  }

  // ---- derived lists ------------------------------------------------------------------------------------------
  private get endPoints(): DodEndPoint[] { return this.definition.endPoints || []; }
  private get subData(): DodEndPointSubData[] { return this.definition.subData || []; }
  private get columns(): DodEndPointColumn[] { return this.definition.columns || []; }

  get errorCount(): number { return this.issues.filter((i) => i.severity === 1).length; }
  get warningCount(): number { return this.issues.filter((i) => i.severity === 0).length; }

  // The endpoint an issue belongs to (null for shared things such as aggregate types).
  issueEndPointId(issue: DodDefinitionIssue): number | null {
    switch (issue.kind) {
      case "EndPoint": return issue.id;
      case "SubData": return this.subData.find((s) => s.dodEndPointSubDataId === issue.id)?.dodEndPointId ?? null;
      case "Column": {
        const c = this.columns.find((x) => x.dodEndPointColumnId === issue.id);
        if (!c) return null;
        return c.dodEndPointId ?? this.subData.find((s) => s.dodEndPointSubDataId === c.dodEndPointSubDataId)?.dodEndPointId ?? null;
      }
      case "Dimension": return (this.definition.dimensions || []).find((d) => d.dodEndPointDimensionId === issue.id)?.dodEndPointId ?? null;
      case "Measure": return (this.definition.measures || []).find((m) => m.dodEndPointMeasureId === issue.id)?.dodEndPointId ?? null;
      default: return null;
    }
  }

  private refresh() {
    const endPoints = this.endPoints;
    const counts = new Map<number, number>();
    for (const i of this.issues.filter((x) => x.severity === 1)) {
      const id = this.issueEndPointId(i);
      if (id !== null) counts.set(id, (counts.get(id) || 0) + 1);
    }
    this.endPointOptions = endPoints
      .filter((e) => this.showRetired || !e.retired || e.dodEndPointId === this.selectedEndPointId)
      .map((e) => ({
        id: e.dodEndPointId,
        label: `${e.title || e.key}${e.retired ? " (retired)" : ""}${counts.get(e.dodEndPointId) ? ` - ${counts.get(e.dodEndPointId)} error(s)` : ""}`,
      }));

    this.selectedEndPoint = endPoints.find((e) => e.dodEndPointId === this.selectedEndPointId) || null;
    const id = this.selectedEndPointId;
    this.endPointIssueCount = id === null ? 0 : this.issues.filter((i) => this.issueEndPointId(i) === id).length;

    this.subDataRows = this.subData.filter((s) => s.dodEndPointId === id && (this.showRetired || !s.retired));
    this.scopeOptions = [{ value: "e", label: "Endpoint columns" }, ...this.subData.filter((s) => s.dodEndPointId === id && (this.showRetired || !s.retired)).map((s) => ({ value: "s:" + s.dodEndPointSubDataId, label: `Sub-data: ${s.title || s.key}` }))];
    if (!this.scopeOptions.some((o) => o.value === this.columnScope)) this.columnScope = "e";

    this.buildColumnGroups();

    const aggregates = this.definition.aggregateTypes || [];
    const links = this.definition.measureAggregates || [];
    this.measureRows = (this.definition.measures || [])
      .filter((m) => m.dodEndPointId === id && (this.showRetired || !m.retired))
      .map((m) => ({
        measure: m,
        aggregates: links.filter((l) => l.dodEndPointMeasureId === m.dodEndPointMeasureId)
          .map((l) => aggregates.find((a) => a.dodEndPointAggregateTypeId === l.dodEndPointAggregateTypeId)?.key || "?").join(", "),
      }));
    this.dimensionRows = (this.definition.dimensions || []).filter((d) => d.dodEndPointId === id && (this.showRetired || !d.retired));
    this.aggregateRows = aggregates.filter((a) => this.showRetired || !a.retired);

    this.filterSelection = new Set((this.definition.endPointFilters || []).filter((f) => f.dodEndPointId === id).map((f) => f.filterTypeId));

    this.moveTargets = [];
    if (id !== null) {
      this.moveTargets.push({ value: "e:" + id, label: "This endpoint (main columns)" });
      for (const s of this.subData.filter((x) => x.dodEndPointId === id)) this.moveTargets.push({ value: "s:" + s.dodEndPointSubDataId, label: "Sub-data: " + (s.title || s.key) });
      for (const e of endPoints.filter((x) => x.dodEndPointId !== id && !x.retired)) this.moveTargets.push({ value: "e:" + e.dodEndPointId, label: "Other endpoint: " + (e.title || e.key) });
    }

    this.testRunning = this.endPoints.some((e) => e.lastTestStatus === "Running") || this.subData.some((s) => s.lastTestStatus === "Running");
    if (this.testRunning) this.startPolling(); else this.stopPolling();

    this.applyIssueFilter();
    const valid = new Set(this.columns.map((c) => c.dodEndPointColumnId));
    Array.from(this.selectedColumnIds).filter((x) => !valid.has(x)).forEach((x) => this.selectedColumnIds.delete(x));
  }

  private columnsInScope(): DodEndPointColumn[] {
    const scope = this.columnScope;
    return this.columns
      .filter((c) => scope === "e" ? c.dodEndPointId === this.selectedEndPointId : c.dodEndPointSubDataId === Number(scope.substring(2)))
      .filter((c) => this.showRetired || !c.retired)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.dodEndPointColumnId - b.dodEndPointColumnId);
  }

  private buildColumnGroups() {
    const groups: ColumnGroup[] = [];
    for (const c of this.columnsInScope()) {
      const name = c.group || "(no group)";
      let g = groups.find((x) => x.name === name);
      if (!g) { g = { name, columns: [] }; groups.push(g); }
      g.columns.push(c);
    }
    this.columnGroups = groups;
  }

  onEndPointChange() {
    this.selectedColumnIds.clear();
    this.moveTargetValue = "";
    this.columnScope = "e";
    this.refresh();
  }

  onScopeChange() {
    this.selectedColumnIds.clear();
    this.buildColumnGroups();
  }

  onShowRetiredChange() {
    this.refresh();
  }

  setIssueFilter(filter: "all" | "errors" | "warnings") {
    this.issueFilter = filter;
    this.applyIssueFilter();
  }

  private applyIssueFilter() {
    this.visibleIssues = this.issues
      .filter((i) => this.issueFilter === "all" || (this.issueFilter === "errors" ? i.severity === 1 : i.severity === 0))
      .sort((a, b) => b.severity - a.severity || (a.subject || "").localeCompare(b.subject || ""));
  }

  goToIssue(issue: DodDefinitionIssue) {
    const endPointId = this.issueEndPointId(issue);
    if (endPointId !== null) {
      this.selectedEndPointId = endPointId;
      this.onEndPointChange();
    }
    switch (issue.kind) {
      case "Column": this.selectedTab = 0; break;
      case "Measure": this.selectedTab = 1; break;
      case "Dimension": this.selectedTab = 2; break;
      case "SubData": this.selectedTab = 3; break;
      case "AggregateType": this.selectedTab = 5; break;
      default: this.selectedTab = 0;
    }
  }

  trackByGroup(_i: number, g: ColumnGroup): string { return g.name; }
  trackByColumn(_i: number, c: DodEndPointColumn): number { return c.dodEndPointColumnId; }

  // ---- shared plumbing -----------------------------------------------------------------------------------------
  private done(result: DodSaveResult | null | undefined, message: string) {
    if (result && result.issues) this.issues = result.issues;
    this.snackBar.open(message, null, { duration: 3000 });
    this.load();
  }

  private failed(error: any) {
    const message = (error && (error.error || error.message)) || "Save failed";
    this.snackBar.open(typeof message === "string" ? message : "Save failed", null, { duration: 8000 });
  }

  validate() {
    this.service.adminDodDefinitionValidate().subscribe({
      next: (issues) => { this.issues = issues; this.load(); },
      error: (e) => this.failed(e),
    });
  }

  setRetired(kind: 0 | 1 | 2 | 3 | 4 | 5, id: number, retired: boolean, label: string) {
    this.service.adminDodDefinitionSetRetired({ kind, id, retired }).subscribe({
      next: (r) => this.done(r, `${label} ${retired ? "retired" : "restored"}`),
      error: (e) => this.failed(e),
    });
  }

  // Hard delete: the server refuses with a specific message when anything live still uses the row.
  deleteRow(kind: 0 | 1 | 2 | 3 | 4 | 5, id: number, label: string, extra = "") {
    if (!confirm(`Delete ${label}? ${extra}This cannot be undone. (If anything still uses it the server will refuse - retire it instead.)`)) return;
    this.service.adminDodDefinitionDelete({ kind, id }).subscribe({
      next: (r) => this.done(r, `${label} deleted`),
      error: (e) => this.failed(e),
    });
  }

  showUsage(kind: 0 | 1 | 2 | 3 | 4, id: number, label: string) {
    this.service.adminDodDefinitionGetUsage({ kind, id }).subscribe({
      next: (u) => {
        const parts: string[] = [];
        if (u.layoutCount > 0) parts.push(`${u.layoutCount} saved layout(s)`);
        if (u.recurringCount > 0) parts.push(`${u.recurringCount} recurring export(s)`);
        if ((u.planTypeIds || []).length > 0) parts.push(`${u.planTypeIds.length} plan type(s)`);
        if (u.jobFileCount > 0) parts.push(`${u.jobFileCount} exported file(s)`);
        this.snackBar.open(parts.length ? `${label} is used by ${parts.join(", ")}.` : `${label} is not used by anything.`, "OK", { duration: 10000 });
      },
      error: (e) => this.failed(e),
    });
  }

  private rename(kind: 0 | 1 | 2 | 3 | 4, id: number, what: string, current: string) {
    DodRenameDialogComponent.showDialog(this.dialog, { kind, id, what, current }).subscribe((result) => {
      if (!result || result.ResultType !== DialogResultType.Ok) return;
      const outcome = result.ResultData && result.ResultData.outcome;
      const message = outcome && outcome.affectedLayoutCount > 0
        ? `Renamed - ${outcome.affectedLayoutCount} saved layout(s) updated`
        : "Renamed";
      this.done(result.ResultData, message);
    });
  }

  // ---- endpoints -----------------------------------------------------------------------------------------------
  private endPointFields(): DodField[] {
    return [
      { key: "key", label: "Key", type: "text", width: 4, hint: "Unique. No commas." },
      { key: "title", label: "Title", type: "text", width: 5 },
      { key: "sortOrder", label: "Sort order", type: "number", width: 3 },
      { key: "type", label: "Type", type: "select", options: ENDPOINT_TYPES, width: 4 },
      { key: "workspaceId", label: "Fabric workspace id", type: "text", emptyAsNull: true, width: 8, hint: "Differs per environment." },
      { key: "notebookId", label: "Fabric notebook id", type: "text", emptyAsNull: true, width: 8, hint: "Differs per environment." },
      { key: "defaultOutputFolderName", label: "Default output folder", type: "text", emptyAsNull: true, width: 8 },
    ];
  }

  newEndPoint() {
    this.openEndPointDialog({ dodEndPointId: 0, key: "", title: "", sortOrder: this.endPoints.length, type: 0, workspaceId: null, notebookId: null, defaultOutputFolderName: null, retired: false });
  }

  editEndPoint() {
    if (this.selectedEndPoint) this.openEndPointDialog({ ...this.selectedEndPoint });
  }

  private openEndPointDialog(model: DodEndPoint) {
    const isNew = !model.dodEndPointId;
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New endpoint" : `Edit endpoint: ${model.title || model.key}`, model, fields: this.endPointFields(),
      deleteLabel: isNew ? undefined : "Delete",
      note: "An endpoint is exported by running its Fabric notebook. Plan-type access is managed in the subscription-type admin.",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const data: DodEndPoint = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) {
        this.deleteRow(ROW_KIND.endPoint, data.dodEndPointId, `endpoint "${data.title || data.key}"`, "Its columns, sub-data, measures, dimensions and filters go with it. ");
        return;
      }
      this.service.adminDodDefinitionSaveEndPoint({ ...data, type: Number(data.type) as 0 | 1 }).subscribe({
        next: (r) => { this.selectedEndPointId = r.id; this.done(r, "Endpoint saved"); },
        error: (e) => this.failed(e),
      });
    });
  }

  toggleEndPointRetired() {
    if (!this.selectedEndPoint) return;
    this.setRetired(ROW_KIND.endPoint, this.selectedEndPoint.dodEndPointId, !this.selectedEndPoint.retired, "Endpoint");
  }

  // ---- sub-data ------------------------------------------------------------------------------------------------
  newSubData() {
    if (this.selectedEndPointId === null) return;
    this.openSubDataDialog({ dodEndPointSubDataId: 0, dodEndPointId: this.selectedEndPointId, key: "", title: "", fileNamePostFix: "", type: 0, workspaceId: null, notebookId: null, defaultOutputFolderName: null, retired: false });
  }

  editSubData(subData: DodEndPointSubData) {
    this.openSubDataDialog({ ...subData });
  }

  private openSubDataDialog(model: DodEndPointSubData) {
    const isNew = !model.dodEndPointSubDataId;
    const fields: DodField[] = [
      { key: "key", label: "Key", type: "text", width: 4, readonly: !isNew, hint: isNew ? "Unique on this endpoint. No commas." : "Use Rename to change it." },
      { key: "title", label: "Title", type: "text", width: 4 },
      { key: "fileNamePostFix", label: "File name postfix", type: "text", width: 4, hint: "Tells its file apart from the endpoint's." },
      { key: "type", label: "Type", type: "select", options: ENDPOINT_TYPES, width: 4 },
      { key: "workspaceId", label: "Fabric workspace id", type: "text", emptyAsNull: true, width: 8 },
      { key: "notebookId", label: "Fabric notebook id", type: "text", emptyAsNull: true, width: 8 },
      { key: "defaultOutputFolderName", label: "Default output folder", type: "text", emptyAsNull: true, width: 8 },
    ];
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New sub-data" : `Edit sub-data: ${model.title || model.key}`, model, fields,
      deleteLabel: isNew ? undefined : "Delete", renameLabel: isNew ? undefined : "Rename key...",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const data: DodEndPointSubData = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) { this.deleteRow(ROW_KIND.subData, data.dodEndPointSubDataId, `sub-data "${data.title || data.key}"`, "Its columns go with it. "); return; }
      if (result.ResultType === DialogResultType.Yes) { this.rename(RENAME_KIND.subDataKey, data.dodEndPointSubDataId, "sub-data key", model.key); return; }
      this.service.adminDodDefinitionSaveSubData({ ...data, type: Number(data.type) as 0 | 1 }).subscribe({
        next: (r) => this.done(r, "Sub-data saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- columns -------------------------------------------------------------------------------------------------
  newColumn() {
    if (this.selectedEndPointId === null) return;
    const scope = this.columnScope;
    const model: DodEndPointColumn = {
      dodEndPointColumnId: 0, dodEndPointId: scope === "e" ? this.selectedEndPointId : null, dodEndPointSubDataId: scope === "e" ? null : Number(scope.substring(2)),
      group: "", name: "", columnSelector: "", description: "", sortOrder: 0, retired: false,
    };
    this.openColumnDialog(model);
  }

  editColumn(column: DodEndPointColumn) {
    this.openColumnDialog({ ...column });
  }

  private openColumnDialog(model: DodEndPointColumn) {
    const isNew = !model.dodEndPointColumnId;
    const fields: DodField[] = [
      { key: "name", label: "Output name", type: "text", width: 6, readonly: !isNew, hint: isNew ? "The column header in the exported file." : "Use Rename output name to change it." },
      { key: "columnSelector", label: "Selector", type: "text", width: 6, readonly: !isNew, hint: isNew ? "SQL expression from the notebook query, e.g. jps.soc_code. No commas, no trailing AS." : "Use Rename selector to change it." },
      { key: "group", label: "Group", type: "text", width: 6, emptyAsNull: true, hint: "Heading users see in the export dialog." },
      { key: "description", label: "Description", type: "textarea", width: 12, emptyAsNull: true },
    ];
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New column" : `Edit column: ${model.name}`, model, fields,
      deleteLabel: isNew ? undefined : "Delete",
      note: "Selectors are exported as \"<selector> AS <output name>\" and must match the alias in this endpoint's notebook query - use Test export to prove it.",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const data: DodEndPointColumn = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) { this.deleteRow(ROW_KIND.column, data.dodEndPointColumnId, `column "${data.name}"`); return; }
      this.service.adminDodDefinitionSaveColumn(data).subscribe({
        next: (r) => this.done(r, "Column saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  renameSelector(c: DodEndPointColumn) { this.rename(RENAME_KIND.columnSelector, c.dodEndPointColumnId, "selector", c.columnSelector || ""); }
  renameColumnName(c: DodEndPointColumn) { this.rename(RENAME_KIND.columnName, c.dodEndPointColumnId, "output name", c.name || ""); }

  // Drag-to-reorder within one group. SortOrder is per owner (not per group), so the whole scope's order is rebuilt with
  // this group's slots refilled in the new order, and that full list is saved.
  dropColumn(group: ColumnGroup, event: CdkDragDrop<DodEndPointColumn[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(group.columns, event.previousIndex, event.currentIndex);
    const all = this.columnGroups.reduce((acc, g) => acc.concat(g.columns), [] as DodEndPointColumn[]);
    const slots = all.map((c, i) => ({ c, i })).filter((x) => group.columns.some((g) => g === x.c || g.dodEndPointColumnId === x.c.dodEndPointColumnId)).map((x) => x.i);
    const ordered = [...all];
    slots.forEach((slot, k) => (ordered[slot] = group.columns[k]));
    this.service.adminDodDefinitionReorder({ kind: ROW_KIND.column, orderedIds: ordered.map((c) => c.dodEndPointColumnId) }).subscribe({
      next: (r) => this.done(r, "Column order saved"),
      error: (e) => this.failed(e),
    });
  }

  // ---- column selection + bulk -----------------------------------------------------------------------------------
  toggleColumn(id: number, checked: boolean) { checked ? this.selectedColumnIds.add(id) : this.selectedColumnIds.delete(id); }
  groupAllSelected(g: ColumnGroup): boolean { return g.columns.length > 0 && g.columns.every((c) => this.selectedColumnIds.has(c.dodEndPointColumnId)); }
  groupSomeSelected(g: ColumnGroup): boolean { return !this.groupAllSelected(g) && g.columns.some((c) => this.selectedColumnIds.has(c.dodEndPointColumnId)); }
  toggleGroup(g: ColumnGroup, checked: boolean) { g.columns.forEach((c) => this.toggleColumn(c.dodEndPointColumnId, checked)); }
  clearSelection() { this.selectedColumnIds.clear(); this.moveTargetValue = ""; this.bulkGroup = ""; }

  bulkRetire(retired: boolean) {
    const columnIds = Array.from(this.selectedColumnIds);
    this.working = true;
    this.service.adminDodDefinitionSetColumnsBulk({ columnIds, retired, changeGroup: false, dryRun: true }).subscribe({
      next: (preview) => {
        const o = preview.outcome;
        const lines = [`${retired ? "Retire" : "Restore"} ${o.changedCount} of ${columnIds.length} selected column(s)?`];
        if (retired && o.affectedLayoutCount > 0) lines.push("", `${o.affectedLayoutCount} saved layout(s) currently select them and will export without them.`);
        if (o.changedCount === 0 || !confirm(lines.join("\n"))) { this.working = false; return; }
        this.service.adminDodDefinitionSetColumnsBulk({ columnIds, retired, changeGroup: false, dryRun: false }).subscribe({
          next: (r) => { this.working = false; this.selectedColumnIds.clear(); this.done(r, `${o.changedCount} column(s) ${retired ? "retired" : "restored"}`); },
          error: (e) => { this.working = false; this.failed(e); },
        });
      },
      error: (e) => { this.working = false; this.failed(e); },
    });
  }

  bulkSetGroup() {
    const columnIds = Array.from(this.selectedColumnIds);
    if (!confirm(`Set the group of ${columnIds.length} column(s) to "${this.bulkGroup.trim() || "(none)"}"?`)) return;
    this.working = true;
    this.service.adminDodDefinitionSetColumnsBulk({ columnIds, changeGroup: true, group: this.bulkGroup.trim(), dryRun: false }).subscribe({
      next: (r) => { this.working = false; this.selectedColumnIds.clear(); this.bulkGroup = ""; this.done(r, "Group updated"); },
      error: (e) => { this.working = false; this.failed(e); },
    });
  }

  get canMove(): boolean { return this.selectedColumnIds.size > 0 && this.moveTargetValue !== "" && !this.working; }

  moveSelected() {
    const target = this.moveTargetValue;
    const request = {
      columnIds: Array.from(this.selectedColumnIds),
      targetEndPointId: target.startsWith("e:") ? Number(target.substring(2)) : undefined,
      targetSubDataId: target.startsWith("s:") ? Number(target.substring(2)) : undefined,
    };
    this.working = true;
    this.service.adminDodDefinitionMoveColumns(request).subscribe({
      next: (r) => { this.working = false; this.selectedColumnIds.clear(); this.moveTargetValue = ""; this.done(r, `Moved ${r.movedCount} column(s)`); },
      error: (e) => { this.working = false; this.failed(e); },
    });
  }

  // ---- measures ------------------------------------------------------------------------------------------------
  newMeasure() {
    if (this.selectedEndPointId === null) return;
    this.openMeasureDialog({ dodEndPointMeasureId: 0, dodEndPointId: this.selectedEndPointId, key: "", title: "", description: "", sortOrder: 0, retired: false }, []);
  }

  editMeasure(row: MeasureRow) {
    const ids = (this.definition.measureAggregates || []).filter((l) => l.dodEndPointMeasureId === row.measure.dodEndPointMeasureId).map((l) => l.dodEndPointAggregateTypeId);
    this.openMeasureDialog({ ...row.measure }, ids);
  }

  private openMeasureDialog(measure: DodEndPointMeasure, aggregateIds: number[]) {
    const isNew = !measure.dodEndPointMeasureId;
    const model: any = { ...measure, aggregateTypeIds: aggregateIds };
    const fields: DodField[] = [
      { key: "key", label: "Key", type: "text", width: 4, readonly: !isNew, hint: isNew ? "No commas or colons." : "Use Rename to change it." },
      { key: "title", label: "Title", type: "text", width: 8 },
      { key: "description", label: "Description", type: "textarea", width: 12, emptyAsNull: true },
      { key: "aggregateTypeIds", label: "Aggregates users can choose", type: "multiselect", width: 12,
        options: (this.definition.aggregateTypes || []).filter((a) => !a.retired || aggregateIds.includes(a.dodEndPointAggregateTypeId)).map((a) => ({ value: a.dodEndPointAggregateTypeId, label: a.key + (a.retired ? " (retired)" : "") })) },
    ];
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New measure" : `Edit measure: ${measure.title || measure.key}`, model, fields,
      deleteLabel: isNew ? undefined : "Delete", renameLabel: isNew ? undefined : "Rename key...",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const { aggregateTypeIds, ...data } = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) { this.deleteRow(ROW_KIND.measure, data.dodEndPointMeasureId, `measure "${data.title || data.key}"`); return; }
      if (result.ResultType === DialogResultType.Yes) { this.rename(RENAME_KIND.measureKey, data.dodEndPointMeasureId, "measure key", measure.key); return; }
      this.service.adminDodDefinitionSaveMeasure(data).subscribe({
        next: (saved) => {
          this.service.adminDodDefinitionSetMeasureAggregates({ measureId: saved.id, aggregateTypeIds: aggregateTypeIds || [] }).subscribe({
            next: (r) => this.done(r, "Measure saved"),
            error: (e) => this.failed(e),
          });
        },
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- dimensions ----------------------------------------------------------------------------------------------
  newDimension() {
    if (this.selectedEndPointId === null) return;
    this.openDimensionDialog({ dodEndPointDimensionId: 0, dodEndPointId: this.selectedEndPointId, key: "", title: "", description: "", sortOrder: 0, retired: false });
  }

  editDimension(d: DodEndPointDimension) { this.openDimensionDialog({ ...d }); }

  private openDimensionDialog(model: DodEndPointDimension) {
    const isNew = !model.dodEndPointDimensionId;
    const fields: DodField[] = [
      { key: "key", label: "Key", type: "text", width: 4, readonly: !isNew, hint: isNew ? "No commas." : "Use Rename to change it." },
      { key: "title", label: "Title", type: "text", width: 8 },
      { key: "description", label: "Description", type: "textarea", width: 12, emptyAsNull: true },
    ];
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New dimension" : `Edit dimension: ${model.title || model.key}`, model, fields,
      deleteLabel: isNew ? undefined : "Delete", renameLabel: isNew ? undefined : "Rename key...",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const data: DodEndPointDimension = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) { this.deleteRow(ROW_KIND.dimension, data.dodEndPointDimensionId, `dimension "${data.title || data.key}"`); return; }
      if (result.ResultType === DialogResultType.Yes) { this.rename(RENAME_KIND.dimensionKey, data.dodEndPointDimensionId, "dimension key", model.key); return; }
      this.service.adminDodDefinitionSaveDimension(data).subscribe({
        next: (r) => this.done(r, "Dimension saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- aggregate types (shared by every endpoint) ---------------------------------------------------------------
  newAggregate() { this.openAggregateDialog({ dodEndPointAggregateTypeId: 0, key: "", description: "", retired: false }); }
  editAggregate(a: DodEndPointAggregateType) { this.openAggregateDialog({ ...a }); }

  private openAggregateDialog(model: DodEndPointAggregateType) {
    const isNew = !model.dodEndPointAggregateTypeId;
    const fields: DodField[] = [
      { key: "key", label: "Key", type: "text", width: 4, hint: "No commas or colons. Cannot change while layouts use it." },
      { key: "description", label: "Description", type: "textarea", width: 12, emptyAsNull: true },
    ];
    DodEditDialogComponent.showDialog(this.dialog, {
      title: isNew ? "New aggregate" : `Edit aggregate: ${model.key}`, model, fields, deleteLabel: isNew ? undefined : "Delete",
      note: "Aggregate types are shared by every endpoint's measures.",
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      const data: DodEndPointAggregateType = result.ResultData;
      if (result.ResultType === DialogResultType.Delete) { this.deleteRow(ROW_KIND.aggregateType, data.dodEndPointAggregateTypeId, `aggregate "${data.key}"`); return; }
      this.service.adminDodDefinitionSaveAggregateType(data).subscribe({
        next: (r) => this.done(r, "Aggregate saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- filters ----------------------------------------------------------------------------------------------------
  toggleFilter(id: number, checked: boolean) { checked ? this.filterSelection.add(id) : this.filterSelection.delete(id); }

  saveFilters() {
    if (this.selectedEndPointId === null) return;
    this.service.adminDodDefinitionSetEndPointFilters({ endPointId: this.selectedEndPointId, filterTypeIds: Array.from(this.filterSelection) }).subscribe({
      next: (r) => this.done(r, "Filters saved"),
      error: (e) => this.failed(e),
    });
  }

  get filterTypes() { return this.definition.filterTypes || []; }

  // ---- test export ------------------------------------------------------------------------------------------------
  // A test is a real Fabric notebook run limited to a few rows with the columns an export would send; it takes minutes, so it
  // runs in the background and this screen polls the definition (the last result is stored on the endpoint / sub-data row).
  private startTest(request: { endPointId?: number; subDataId?: number; columnIds?: number[] }, what: string) {
    this.testStarting = true;
    this.testService.adminDodTestStart(request).subscribe({
      next: (r) => {
        this.testStarting = false;
        this.snackBar.open(`Testing ${r.columnsToTest} column(s) of ${what} - this takes a few minutes`, null, { duration: 5000 });
        this.load();
      },
      error: (e) => {
        this.testStarting = false;
        this.failed(e);
      },
    });
  }

  testEndPoint() {
    if (!this.selectedEndPoint) return;
    this.startTest({ endPointId: this.selectedEndPoint.dodEndPointId }, `endpoint ${this.selectedEndPoint.title || this.selectedEndPoint.key}`);
  }

  testSubData(s: DodEndPointSubData) {
    this.startTest({ subDataId: s.dodEndPointSubDataId }, `sub-data ${s.title || s.key}`);
  }

  // Tests just the ticked columns of whatever the Columns tab is showing (the endpoint's own columns, or one sub-data's).
  testSelectedColumns() {
    if (this.selectedEndPointId === null || this.selectedColumnIds.size === 0) return;
    const columnIds = Array.from(this.selectedColumnIds);
    if (this.columnScope === "e") this.startTest({ endPointId: this.selectedEndPointId, columnIds }, "the selected columns");
    else this.startTest({ subDataId: Number(this.columnScope.substring(2)), columnIds }, "the selected columns");
  }

  private startPolling() {
    if (this.testPoll) return;
    this.testPoll = setInterval(() => this.load(true), 6000);
  }

  private stopPolling() {
    if (this.testPoll) { clearInterval(this.testPoll); this.testPoll = null; }
  }

  testBadgeClass(status: string | undefined): string {
    switch (status) {
      case "Passed": return "badge badge-success";
      case "Failed": return "badge badge-danger";
      case "Running": return "badge badge-info";
      default: return "badge badge-secondary";
    }
  }
}
